import React, { useEffect } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import styles from './EditVoucherThemeModal.module.scss';
import { toast } from 'react-toastify';
import { updateVoucherTheme } from '../../api/voucherTheme';
import { getVoucherThemeCategories } from '../../api/voucherTheme';
import useComponentState from '../../utils/useComponentState';

const EditVoucherThemeModal = ({ show, onHide, theme, token, onSuccess }) => {
  const [state, setState] = useComponentState({
    ...theme,
    categories: [],
  });

  useEffect(() => {
    if (theme) {
      setState((prev) => ({
        ...theme,
        categories: prev.categories,
      }));
    }
  }, [theme]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getVoucherThemeCategories(token);
      if (response && response.voucherThemeCategories) {
        setState((prev) => ({
          ...prev,
          categories: response.voucherThemeCategories,
        }));
      }
    } catch (error) {
      toast.error('Failed to load voucher theme categories');
    }
  };

  const handlePublishUnpublish = async () => {
    const payload = {
      voucherThemeId: state.id,
      isPublished: !state.isPublished,
    };

    try {
      const response = await updateVoucherTheme(payload, token);
      if (response.message === 'VOUCHER_THEME_UPDATED_SUCCESSFULLY') {
        onSuccess();
        onHide();
        toast.success(
          `Voucher theme ${
            state.isPublished === 'Published' ? 'Published' : 'Unpublished'
          } successfully`
        );
        setState((prev) => ({
          ...prev,
          isPublished: !prev.isPublished,
        }));
      }
    } catch (error) {
      console.error('Error updating voucher theme:', error);
      toast.error('Failed to update voucher theme');
    }
  };

  const handleVoucherThemeUpdate = async () => {
    const payload = {
      voucherThemeId: state.id,
      voucherThemeCategoryId: state.selectedCategoryId
        ? state.selectedCategoryId
        : state.voucherThemeCategory?.id,
      themeName: state.name,
      smsContent: state.smsContent,
      emailContent: state.emailContent,
      emailSubject: state.emailSubject,
      backgroundImageUrl: state.imageBack,
      thumbnailImageUrl: state.thumbnail,
      imageFront: state.imageFront,
      imageBack: state.imageBack,
      mobileImageUrl: state.mobile,
    };

    try {
      const response = await updateVoucherTheme(payload, token);
      if (response.message === 'VOUCHER_THEME_UPDATED_SUCCESSFULLY') {
        onSuccess();
        onHide();
        toast.success(`Voucher theme updated successfully`);
        setState({
          voucherThemeId: '',
          voucherThemeCategoryId: '',
          themeName: '',
          smsContent: '',
          emailContent: '',
          emailSubject: '',
          backgroundImageUrl: '',
          thumbnailImageUrl: '',
          imageFront: '',
          imageBack: '',
          mobileImageUrl: '',
        });
      }
    } catch (error) {
      console.error('Error updating voucher theme:', error);
      toast.error('Failed to update voucher theme');
    }
  };

  // Reusable input field
  const renderInput = ({
    id,
    label,
    type = 'text',
    placeholder,
    value,
    onChange,
    rows,
  }) => (
    <Form.Group className='mb-3' controlId={id}>
      <Form.Label>{label}</Form.Label>
      {rows ? (
        <Form.Control
          as='textarea'
          rows={rows}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
      ) : (
        <Form.Control
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
      )}
    </Form.Group>
  );

  return (
    <Modal
      show={show}
      className={styles.editMerchantModal}
      onHide={onHide}
      size='lg'
      centered
      backdrop={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Voucher Theme</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* <div className={styles.buttonContainer}>
          <Button
            variant='secondary'
            size='sm'
            onClick={handlePublishUnpublish}
          >
            {state.isPublished === 'Published' ? 'Unpublish' : 'Publish'}
          </Button>
        </div> */}
        <Form>
          {renderInput({
            id: 'themeName',
            label: 'Theme Name',
            placeholder: 'Enter theme name',
            value: state.name,
            onChange: (e) => setState({ name: e.target.value }),
          })}

          <Form.Group className='mb-3' controlId='category'>
            <Form.Label>Voucher Theme Category</Form.Label>
            <Form.Control
              as='select'
              value={state.voucherThemeCategory?.id ?? ''}
              onChange={(e) => {
                const value = Number(e.target.value);
                const selectedCategory = state.categories.find(
                  (c) => c.id === value
                );
                setState((prev) => ({
                  ...prev,
                  voucherThemeCategory: {
                    id: value,
                    name: selectedCategory?.name,
                    defaultThemeId: selectedCategory?.defaultThemeId,
                  },
                  selectedCategoryId: value,
                }));
              }}
            >
              <option value='' disabled>
                Select a category
              </option>
              {state.categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {renderInput({
            id: 'imageFront',
            label: 'Front Image URL',
            placeholder: 'Enter front image URL',
            value: state.imageFront,
            onChange: (e) => setState({ imageFront: e.target.value }),
          })}

          {renderInput({
            id: 'imageBack',
            label: 'Back Image URL',
            placeholder: 'Enter back image URL',
            value: state.imageBack,
            onChange: (e) => setState({ imageBack: e.target.value }),
          })}

          {renderInput({
            id: 'mobileImageUrl',
            label: 'Mobile Image URL',
            placeholder: 'Enter mobile image URL',
            value: state.mobile,
            onChange: (e) => setState({ mobile: e.target.value }),
          })}
          {renderInput({
            id: 'backgroundImageUrl',
            label: 'Background Image URL',
            placeholder: 'Enter background image URL',
            value: state.background,
            onChange: (e) => setState({ background: e.target.value }),
          })}
          {renderInput({
            id: 'thumbnailImageUrl',
            label: 'Thumbnail Image URL',
            placeholder: 'Enter thumbnail image URL',
            value: state.thumbnail,
            onChange: (e) => setState({ thumbnail: e.target.value }),
          })}

          {renderInput({
            id: 'emailSubject',
            label: 'Email Subject',
            placeholder: 'Enter email subject',
            value: state.emailSubject,
            onChange: (e) => setState({ emailSubject: e.target.value }),
          })}

          {renderInput({
            id: 'emailContent',
            label: 'Email Content',
            placeholder: 'Enter email content',
            value: state.emailContent,
            onChange: (e) => setState({ emailContent: e.target.value }),
            rows: 6,
          })}

          {renderInput({
            id: 'smsContent',
            label: 'SMS Content',
            placeholder: 'Enter SMS content',
            value: state.smsContent,
            onChange: (e) => setState({ smsContent: e.target.value }),
            rows: 6,
          })}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Close
        </Button>
        <Button variant='primary' onClick={handleVoucherThemeUpdate}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditVoucherThemeModal;
